import React, { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import { toast, Toaster } from 'react-hot-toast';
import { FaGithub, FaLinkedin, FaMedium, FaMapMarkerAlt, FaPhone, FaEnvelope } from 'react-icons/fa';

const Contact = () => {
  const formRef = useRef<HTMLFormElement>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formRef.current) return;
    
    try {
      setLoading(true);
      
      await emailjs.sendForm(
        import.meta.env.VITE_EMAILJS_SERVICE_ID,
        import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
        formRef.current,
        import.meta.env.VITE_EMAILJS_PUBLIC_KEY
      );

      toast.success('Message sent successfully!');
      formRef.current.reset();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-20 bg-[#0a0a0f] border-t border-white/5">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12" data-aos="fade-up">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-light text-white mb-3">Contact</h2>
          <p className="text-gray-400 text-lg">Get in touch</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Contact Form */}
          <div className="bg-white/5 border border-white/10 p-6 md:p-8 rounded-lg">
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              className="space-y-5"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-400 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  name="user_name"
                  id="name"
                  required
                  className="w-full px-4 py-3 rounded bg-white/5 border border-white/10 text-gray-300 placeholder-gray-500 focus:outline-none focus:border-white/20 focus:bg-white/10 transition-all duration-300"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="user_email"
                  id="email"
                  required
                  className="w-full px-4 py-3 rounded bg-white/5 border border-white/10 text-gray-300 placeholder-gray-500 focus:outline-none focus:border-white/20 focus:bg-white/10 transition-all duration-300"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-400 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  required
                  className="w-full px-4 py-3 rounded bg-white/5 border border-white/10 text-gray-300 placeholder-gray-500 focus:outline-none focus:border-white/20 focus:bg-white/10 transition-all duration-300 resize-none"
                  placeholder="Your message..."
                />
              </div>

              <div className="flex justify-center pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-3 bg-white text-[#0a0a0f] rounded-lg font-medium hover:bg-gray-200 focus:outline-none transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>

          {/* Contact Information */}
          <div className="bg-white/5 border border-white/10 p-6 md:p-8 rounded-lg" data-aos="fade-up" data-aos-delay="300">
            <h3 className="text-xl font-medium text-white mb-6">Contact Information</h3>
            <div className="space-y-4 mb-8">
              <div className="flex items-center text-gray-400">
                <FaMapMarkerAlt className="w-4 h-4 mr-3" />
                <span>Kathmandu, Nepal</span>
              </div>
              <div className="flex items-center text-gray-400">
                <FaPhone className="w-4 h-4 mr-3" />
                <span>+977 9827489335</span>
              </div>
              <div className="flex items-center text-gray-400">
                <FaEnvelope className="w-4 h-4 mr-3" />
                <span>dev.roshankarki@gmail.com</span>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-medium text-white mb-4">Follow Me</h3>
              <div className="flex gap-3">
                <a
                  href="https://github.com/roshankc00"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 rounded border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition-all duration-300"
                  aria-label="GitHub"
                >
                  <FaGithub className="w-5 h-5" />
                </a>
                <a
                  href="https://www.linkedin.com/in/roshan-karki-a28744273/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 rounded border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition-all duration-300"
                  aria-label="LinkedIn"
                >
                  <FaLinkedin className="w-5 h-5" />
                </a>
                <a
                  href="https://medium.com/@dev.roshankarki"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 rounded border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition-all duration-300"
                  aria-label="Medium"
                >
                  <FaMedium className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Toaster position="bottom-right" />
    </section>
  );
};

export default Contact;
